import { useNavigate, useSearchParams } from "react-router-dom";
import { useEffect, useState, Fragment } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Search, Plus, Edit3, Trash2, Users, ChevronDown, ChevronRight } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { norm } from "@/lib/normalize";
import { useLookups } from "@/context/LookupContext";
import { formatINR } from "@/lib/currency";
import { formatDate } from "@/lib/datetime";
import { useConfirm } from "@/components/ConfirmDialog";
import TablePager from "@/components/TablePager";

const EMPTY = { firstName: "", lastName: "", email: "", phone: "", gender: "", source: "", membershipPlanId: "", referredByStaffId: "" };

const ExpandIcon = ({ expanded }) =>
  expanded ? <ChevronDown className="h-4 w-4 text-indigo-500" /> : <ChevronRight className="h-4 w-4 text-indigo-500" />;

export default function Customers() {
  const { user } = useAuth();
  const { lookups } = useLookups();
  const isStaff = norm(user.roleName) === "admin" || norm(user.roleName) === "staff" || norm(user.roleName) === "receptionist" || norm(user.roleName) === "manager";
  const isAdmin = norm(user.roleName) === "admin";
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [filterSource, setFilterSource] = useState("ALL");
  const [filterMembership, setFilterMembership] = useState("ALL");
  const [filterVisits, setFilterVisits] = useState("ALL");
  const [open, setOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [staffList, setStaffList] = useState([]);
  const [expanded, setExpanded] = useState({});
  const [history, setHistory] = useState({}); // { [customerId]: { loading, error, items } }
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const navigate = useNavigate();

  const toggleHistory = (customerId) => {
    const isOpen = !!expanded[customerId];
    setExpanded((e) => ({ ...e, [customerId]: !isOpen }));
    // Fetch on first expand only
    if (!isOpen && !history[customerId]) {
      setHistory((h) => ({ ...h, [customerId]: { loading: true, error: false, items: [] } }));
      api.get(`/customers/${customerId}/history`)
        .then((r) => setHistory((h) => ({ ...h, [customerId]: { loading: false, error: false, items: Array.isArray(r.data) ? r.data : (r.data?.invoices || []) } })))
        .catch(() => setHistory((h) => ({ ...h, [customerId]: { loading: false, error: true, items: [] } })));
    }
  };

  const load = () => api.get("/customers").then((r) => setRows(r.data)).catch(() => toast.error("Failed"));
  useEffect(() => { load(); }, []);

  useEffect(() => {
    api.get("/staff").then((r) => setStaffList(r.data)).catch(() => {});
  }, []);

  // useEffect(() => {
  //   api.get("/customers").then((r) => setRows(r.data)).catch(() => toast.error("Failed"));
  // }, []);

  const filtered = rows.filter((r) => {
    const fullName = ((r.firstName || "") + " " + (r.lastName || "")).toLowerCase();
    const phone = (r.phone || "").toLowerCase();
    const search = q.toLowerCase();
    if (!(fullName.includes(search) || phone.includes(search))) return false;

    if (filterSource !== "ALL" && (r.source || "") !== filterSource) return false;

    if (filterMembership !== "ALL") {
      if (filterMembership === "NONE") {
        if (r.membershipPlanId) return false;
      } else if (String(r.membershipPlanId || "") !== filterMembership) {
        return false;
      }
    }

    if (filterVisits !== "ALL") {
      const v = Number(r.numberOfVisits || 0);
      if (filterVisits === "0" && v !== 0) return false;
      if (filterVisits === "1-2" && !(v >= 1 && v <= 2)) return false;
      if (filterVisits === "3-5" && !(v >= 3 && v <= 5)) return false;
      if (filterVisits === "6+" && v < 6) return false;
    }
    return true;
  });

  // Sort alphabetically by full name (case-insensitive).
  const sorted = [...filtered].sort((a, b) => {
    const na = `${a.firstName || ""} ${a.lastName || ""}`.trim().toLowerCase();
    const nb = `${b.firstName || ""} ${b.lastName || ""}`.trim().toLowerCase();
    return na.localeCompare(nb);
  });

  const pageCount = Math.max(1, Math.ceil(sorted.length / pageSize));
  const currentPage = Math.min(page, pageCount - 1);
  const pagedCustomers = sorted.slice(currentPage * pageSize, currentPage * pageSize + pageSize);

  // Reset to the first page whenever filters/search change the result set.
  useEffect(() => { setPage(0); }, [q, filterSource, filterMembership, filterVisits, pageSize]);

  const goAppointmentsById = (id) => {
    const params = new URLSearchParams();
    if (id) params.set("appointment", id);
    const url = `/dashboard/appointments?${params.toString()}`;
    navigate(url);
  };

  const openAdd = () => { setEditingId(null); setForm(EMPTY); setOpen(true); };
  const openEdit = (c) => {
    setEditingId(c.id);
    setForm({ firstName: c.firstName || "", lastName: c.lastName || "", email: c.email || "", phone: c.phone || "", gender: c.gender || "", source: c.source || "", membershipPlanId: c.membershipPlanId ? String(c.membershipPlanId) : "", walletBalance: c.walletBalance || 0, lastVisited: c.lastVisited || "", referredByStaffId: c.referredByStaffId ? String(c.referredByStaffId) : "" });
    setOpen(true);
  };

  const confirm = useConfirm();
  const del = async (id) => {
    if (!(await confirm("Delete this customer?"))) return;
    try { await api.delete(`/customers/${id}`); toast.success("Deleted"); load(); }
    catch { toast.error("Failed"); }
  };

  const save = async (e) => {
    e.preventDefault();
    if (!form.firstName.trim()) { toast.error("First Name required"); return; }
    setSaving(true);
    try {
      const payload = { ...form, membershipPlanId: form.membershipPlanId ? Number(form.membershipPlanId) : null, referredByStaffId: form.referredByStaffId ? Number(form.referredByStaffId) : null, walletBalance: Number(form.walletBalance || 0) };
      if (editingId) {
        await api.put(`/customers/${editingId}`, payload);
        toast.success("Customer updated");
      } else {
        await api.post("/customers", payload);
        toast.success("Customer added");
      }
      setOpen(false); setForm(EMPTY); setEditingId(null);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-8" data-testid="customers-page">
      <div className="flex items-end justify-between gap-4">
        <div>
          <div className="overline mb-2">CRM</div>
          <h1 className="font-heading text-4xl text-white inline-flex items-center gap-3">
            <Users className="w-8 h-8 text-amber-500" /> Customers
            <span className="text-base font-semibold text-gold bg-[rgba(197,160,89,0.15)] border border-gold rounded-full px-3 py-1" style={{ color: "#E4B84C" }} data-testid="customer-count">
              {filtered.length === rows.length ? filtered.length : `${filtered.length} of ${rows.length}`}
            </span>
          </h1>
        </div>
        {isStaff && (
          <button data-testid="add-customer-btn" onClick={openAdd}
            className="gold-button px-5 py-2.5 rounded-full inline-flex items-center gap-2">
            <Plus className="w-4 h-4" /> Add Customer
          </button>
        )}
      </div>
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative max-w-sm flex-1 min-w-[220px]">
          <Search className="w-4 h-4 text-emerald-muted absolute left-3 top-1/2 -translate-y-1/2" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search customers…"
            className="pl-9 bg-emerald-surface border-gold-subtle text-white" data-testid="customer-search" />
        </div>
        <Select value={filterSource} onValueChange={setFilterSource}>
          <SelectTrigger className="bg-emerald-surface border-gold-subtle text-white h-10 w-48" data-testid="customer-source-filter">
            <SelectValue placeholder="All Sources" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="ALL">All Sources</SelectItem>
            {(lookups?.customerSources || []).map((s) => (
              <SelectItem key={s.name || s} value={s.name || s}>{s.name || s}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={filterMembership} onValueChange={setFilterMembership}>
          <SelectTrigger className="bg-emerald-surface border-gold-subtle text-white h-10 w-48" data-testid="customer-membership-filter">
            <SelectValue placeholder="All Memberships" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="ALL">All Memberships</SelectItem>
            <SelectItem value="NONE">No Membership</SelectItem>
            {(lookups?.membershipPlans || []).map((p) => (
              <SelectItem key={p.id} value={String(p.id)}>{p.planName}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={filterVisits} onValueChange={setFilterVisits}>
          <SelectTrigger className="bg-emerald-surface border-gold-subtle text-white h-10 w-40" data-testid="customer-visits-filter">
            <SelectValue placeholder="All Visits" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="ALL">All Visits</SelectItem>
            <SelectItem value="0">No visits</SelectItem>
            <SelectItem value="1-2">1–2 visits</SelectItem>
            <SelectItem value="3-5">3–5 visits</SelectItem>
            <SelectItem value="6+">6+ visits</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <div className="bg-emerald-surface border border-gold-subtle rounded-2xl overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="thead-row hover:bg-[rgba(197,160,89,0.06)]">
              <TableHead className="th-gold w-8"></TableHead>
              <TableHead className="th-gold">Name</TableHead>
              <TableHead className="th-gold">Phone</TableHead>
              <TableHead className="th-gold">Visits</TableHead>
              <TableHead className="th-gold">Last Visited</TableHead>
              <TableHead className="th-gold text-right">Wallet Balance</TableHead>
              <TableHead className="th-gold text-right">Appointments</TableHead>
              {isStaff && <TableHead className="th-gold w-24 text-right">Action</TableHead>}
            </TableRow>
          </TableHeader>
          <TableBody>
            {sorted.length === 0 && <TableRow><TableCell colSpan={isStaff ? 8 : 7} className="text-center text-emerald-muted py-8">No customers found.</TableCell></TableRow>}
            {pagedCustomers.map((c) => {
              const isOpen = !!expanded[c.id];
              const hist = history[c.id];
              const colCount = isStaff ? 8 : 7;
              return (
              <Fragment key={c.id}>
              <TableRow className="border-gold-subtle"
                data-testid={`customer-row-${c.id}`}>
                <TableCell className="w-8">
                  <button
                    onClick={() => toggleHistory(c.id)}
                    data-testid={`customer-expand-${c.id}`}
                    className="p-1 rounded hover:bg-emerald-elev/60"
                    title="View invoice history">
                    <ExpandIcon expanded={isOpen} />
                  </button>
                </TableCell>
                <TableCell className="text-white">{[c.firstName, c.lastName].filter(Boolean).join(" ")}</TableCell>
                <TableCell className="text-emerald-muted">{c.phone || "—"}</TableCell>
                <TableCell className="text-emerald-muted">{c.numberOfVisits ?? "—"}</TableCell>
                <TableCell className="text-emerald-text">{formatDate(c.lastVisited)}</TableCell>
                <TableCell className="text-right text-gold">{formatINR(c.walletBalance || 0)}</TableCell>
                <TableCell className="text-emerald-muted text-gold text-right">
                    {c.appointmentCount > 0 && (
                      <button onClick={() => goAppointmentsById(c.id)} data-testid={`go-to-appt-${c.id}`} className="text-emerald-muted-gold">{c.appointmentCount}</button>
                    )}
                </TableCell>
                {isStaff && (
                  <TableCell className="text-right">
                    <button onClick={() => openEdit(c)} className="text-emerald-muted hover:text-gold p-1"><Edit3 className="w-4 h-4" /></button>
                    {isAdmin && (
                      <button onClick={() => del(c.id)} className="text-emerald-muted hover:text-red-300 p-1 ml-2" data-testid={`delete-customer-${c.id}`}><Trash2 className="w-4 h-4" /></button>
                    )}
                  </TableCell>
                )}
              </TableRow>
              {isOpen && (
                <TableRow className="border-gold-subtle hover:bg-transparent">
                  <TableCell colSpan={colCount} className="p-0">
                    <div className="p-4 bg-emerald-elev/30">
                      <div className="mb-3 flex items-center justify-between gap-3">
                        <span className="text-indigo-400 text-xs font-bold uppercase tracking-[0.24em]">Invoice History</span>
                        {hist && !hist.loading && !hist.error && hist.items.length > 0 && (
                          <span className="text-xs text-emerald-muted">
                            Total: <span className="text-gold font-semibold">{formatINR(hist.items.reduce((sum, inv) => sum + (inv.totalAmount || 0), 0))}</span>
                          </span>
                        )}
                      </div>
                      {hist?.loading && <div className="text-sm text-emerald-muted py-4 text-center">Loading…</div>}
                      {hist?.error && <div className="text-sm text-red-300 py-4 text-center">Failed to load history.</div>}
                      {hist && !hist.loading && !hist.error && hist.items.length === 0 && (
                        <div className="text-sm text-emerald-muted py-4 text-center">No invoices for this customer.</div>
                      )}
                      {hist && !hist.loading && !hist.error && hist.items.length > 0 && (
                        <div className="border border-gold-subtle rounded-lg overflow-hidden">
                          <Table>
                            <TableHeader>
                              <TableRow className="thead-row hover:bg-[rgba(197,160,89,0.06)]">
                                <TableHead className="th-gold">Invoice No.</TableHead>
                                <TableHead className="th-gold">Date</TableHead>
                                <TableHead className="th-gold">Staff</TableHead>
                                <TableHead className="th-gold text-right">Total</TableHead>
                              </TableRow>
                            </TableHeader>
                            <TableBody>
                              {hist.items.map((inv, i) => (
                                <TableRow key={inv.invoiceNo || i} className="border-gold-subtle" data-testid={`customer-invoice-${c.id}-${i}`}>
                                  <TableCell className="text-white">{inv.invoiceNo || "—"}</TableCell>
                                  <TableCell className="text-emerald-muted">{formatDate(inv.invoiceDate)}</TableCell>
                                  <TableCell className="text-emerald-muted">{inv.staff || "—"}</TableCell>
                                  <TableCell className="text-right text-gold">{formatINR(inv.totalAmount || 0)}</TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </div>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              )}
              </Fragment>
              );
            })}
          </TableBody>
        </Table>
        {sorted.length > 0 && (
          <TablePager
            page={currentPage}
            pageSize={pageSize}
            total={sorted.length}
            onPageChange={setPage}
            onPageSizeChange={setPageSize}
          />
        )}
      </div>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-emerald-surface border-gold-subtle text-white max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="customer-modal">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl">
              {editingId ? "Edit Customer" : "Add Customer"}
            </DialogTitle>
            <DialogDescription className="text-emerald-muted">
              {editingId ? "Update customer details." : "Create a new customer record."}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={save} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>First Name</Label>
                <Input value={form.firstName} onChange={(e) => setForm({ ...form, firstName: e.target.value })}
                  className="bg-emerald-elev border-gold-subtle mt-1" data-testid="customer-fname-input" required />
              </div>
              <div>
                <Label>Last Name</Label>
                <Input value={form.lastName} onChange={(e) => setForm({ ...form, lastName: e.target.value })}
                  className="bg-emerald-elev border-gold-subtle mt-1" data-testid="customer-lname-input" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Email</Label>
                <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
                  className="bg-emerald-elev border-gold-subtle mt-1" data-testid="customer-email-input" />
              </div>
              <div>
                <Label>Phone</Label>
                <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })}
                  className="bg-emerald-elev border-gold-subtle mt-1" data-testid="customer-phone-input" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Gender</Label>
                <Select value={form.gender} onValueChange={(val) => setForm({ ...form, gender: val })}>
                  <SelectTrigger className="mt-1 bg-emerald-elev border-gold-subtle text-white h-9" data-testid="customer-gender-input">
                    <SelectValue placeholder="Select gender" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="MALE">Male</SelectItem>
                    <SelectItem value="FEMALE">Female</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Source</Label>
                <Select value={form.source} onValueChange={(val) => setForm({ ...form, source: val, referredByStaffId: (val === "EMPLOYEE REFERRAL" || val === "EMPLOYEE_REFERRAL") ? form.referredByStaffId : "" })}>
                  <SelectTrigger className="mt-1 bg-emerald-elev border-gold-subtle text-white h-9" data-testid="customer-source-input">
                    <SelectValue placeholder="How did they find us?" />
                  </SelectTrigger>
                  <SelectContent>
                    {(lookups?.customerSources || []).map((s) => (
                      <SelectItem key={s.name || s} value={s.name || s}>{s.name || s}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            {(form.source === "EMPLOYEE REFERRAL" || form.source === "EMPLOYEE_REFERRAL") && (
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Referred By</Label>
                  <Select value={form.referredByStaffId} onValueChange={(val) => setForm({ ...form, referredByStaffId: val })}>
                    <SelectTrigger className="mt-1 bg-emerald-elev border-gold-subtle text-white h-9" data-testid="customer-referred-staff-input">
                      <SelectValue placeholder="Select employee" />
                    </SelectTrigger>
                    <SelectContent>
                      {staffList.map((s) => (
                        <SelectItem key={s.id} value={String(s.id)}>{s.firstName || s.name || [s.firstName, s.lastName].filter(Boolean).join(" ")}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                {editingId && (
                  <div>
                    <Label>Last Visited</Label>
                    <Input value={formatDate(form.lastVisited)} disabled className="bg-emerald-elev border-gold-subtle mt-1 text-emerald-muted" data-testid="customer-last-visited" />
                  </div>
                )}
              </div>
            )}
            {!(form.source === "EMPLOYEE REFERRAL" || form.source === "EMPLOYEE_REFERRAL") && editingId && (
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Last Visited</Label>
                  <Input value={formatDate(form.lastVisited)} disabled className="bg-emerald-elev border-gold-subtle mt-1 text-emerald-muted" data-testid="customer-last-visited" />
                </div>
              </div>
            )}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Membership Plan</Label>
                <Select value={form.membershipPlanId} onValueChange={(val) => setForm({ ...form, membershipPlanId: val })}>
                  <SelectTrigger className="mt-1 bg-emerald-elev border-gold-subtle text-white h-9" data-testid="customer-membership-input">
                    <SelectValue placeholder="Select membership" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No Membership</SelectItem>
                    {(lookups?.membershipPlans || []).map((p) => (
                      <SelectItem key={p.id} value={String(p.id)}>{p.planName}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              {editingId && (
                <div>
                  <Label>Wallet Balance (₹)</Label>
                  <Input type="number" step="1000" value={form.walletBalance ?? ""} 
                    onChange={(e) => setForm({ ...form, walletBalance: e.target.value })}
                    disabled={!isAdmin}
                    placeholder="Enter amount"
                    className="bg-emerald-elev border-gold-subtle mt-1 text-gold" data-testid="customer-wallet-balance" />
                </div>
              )}
            </div>
            <button type="submit" disabled={saving}
              className="gold-button w-full h-11 rounded-md disabled:opacity-60"
              data-testid="save-customer-btn">
              {saving ? "Saving…" : (editingId ? "Save Changes" : "Add Customer")}
            </button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
